/**
 * Office / Template mismatch guard (RW-772).
 *
 * Soft-warn (never block) when an onboarding template scoped to one office is
 * about to be assigned to an agent whose home office differs — e.g. a New
 * Orleans template to a Baton Rouge agent. Per the UX principle "forgiveness
 * over prevention" (CLAUDE.md), the caller renders a warning the admin can
 * override rather than disabling the action.
 *
 * Office values are NOT stored uniformly across the app: the onboarding
 * templates store a display NAME ("New Orleans"), while user docs written by
 * the profile wizard / ProfileEditor store an office ID ("new-orleans"). A
 * naive string compare therefore reports a mismatch on EVERY office-scoped
 * assignment. To compare correctly we canonicalize both sides through the
 * shared office catalog (src/lib/offices.js), which knows both the id and the
 * name, plus the stale "Northshore" → Covington alias (RW-455). Values not in
 * the catalog fall back to a normalized-string compare.
 *
 * Two cases are deliberately NOT a mismatch:
 *   - the template has no office → it's "global" and matches every agent
 *   - the agent has no recorded office → we can't judge a mismatch, so stay quiet
 *
 * Used by the staff onboarding create door: AssignOnboardingWizard (RW-1239 —
 * the one modal shared by AdminOnboardingTab's List and OnboardingPipeline's
 * Board, which replaced the two divergent modals that lived in those files).
 */
import { OFFICES as OFFICE_CATALOG } from './offices';

/** Normalize an office string for comparison: trim + collapse inner whitespace + lowercase. */
export function normalizeOffice(office) {
  return String(office || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Map any office value (id like "new-orleans", display name like "New Orleans"
 * or "Covington-Northshore", or the stale "Northshore") to a canonical office
 * id. Returns the normalized string unchanged when the value isn't a known
 * office, so unknown/custom offices still compare sensibly against each other.
 */
export function canonicalOfficeKey(office) {
  const n = normalizeOffice(office);
  if (!n) return '';
  // Direct id match (catalog is keyed by id, e.g. "new-orleans").
  if (OFFICE_CATALOG[n]) return n;
  // Match by id or display name (case-insensitively).
  for (const o of Object.values(OFFICE_CATALOG)) {
    if (normalizeOffice(o.id) === n || normalizeOffice(o.name) === n) return o.id;
  }
  // Known alias: the Northshore office was renamed to Covington (RW-455), but
  // older templates / the profile label "Covington-Northshore" still say it.
  if (n.includes('northshore')) return 'covington';
  // RW-2558: Darwin's roster names the New Orleans office by its street —
  // "Prytania Street" (darwin_agents.office_id 2). Same office; the leaderboard
  // reads the roster office for agents whose profile never had one set.
  if (n.includes('prytania')) return 'new-orleans';
  return n;
}

/**
 * Look up a template's officeOverrides entry for an office, canonicalizing
 * BOTH sides so display-name keys and slug keys resolve to the same bucket
 * (RW-1632 T2-02). Only array-shaped entries are consumable ([{ sectionId,
 * item }] — RW-1200); the retired seed placeholder ({ additionalItems,
 * removedItemIds }, T2-08) and any other non-array shape return [] instead of
 * crashing callers. Server twin: functions/src/util/officeKeys.js.
 */
export function officeOverridesFor(template, office) {
  const overrides = (template && template.officeOverrides) || {};
  const want = canonicalOfficeKey(office);
  if (!want) return [];
  for (const [key, value] of Object.entries(overrides)) {
    if (canonicalOfficeKey(key) === want) {
      return Array.isArray(value) ? value : [];
    }
  }
  return [];
}

/** Human-friendly office label for display: catalog name when known, else the raw value. */
export function prettyOffice(office) {
  const key = canonicalOfficeKey(office);
  return OFFICE_CATALOG[key]?.name || String(office || '');
}

/**
 * The agent's home office, honoring the legacy `primaryOffice` fallback.
 * (New code writes `office`; older profile-wizard docs wrote `primaryOffice`.)
 */
export function getAgentOffice(agent) {
  if (!agent) return '';
  return agent.office || agent.primaryOffice || '';
}

/**
 * Canonical office key for a user doc. Prefers the canonical `officeId`
 * (written by the RW-454 backfill and read by office badges/filters), falling
 * back to the legacy `office`/`primaryOffice` values that docs the backfill
 * missed still carry (RW-1484) — those may hold a display name, which
 * canonicalOfficeKey resolves to the slug.
 */
export function userOfficeKey(user) {
  if (!user) return '';
  return canonicalOfficeKey(user.officeId || getAgentOffice(user));
}

/**
 * Decide whether assigning `template` to `agent` is an office mismatch.
 * Returns a stable shape so callers can render the banner without recomputing;
 * `templateOffice`/`agentOffice` are pretty labels suitable for display.
 *
 * @param {{ office?: string }} template - onboarding template doc
 * @param {{ office?: string, primaryOffice?: string }} agent - target user doc
 * @returns {{ mismatch: boolean, templateOffice: string, agentOffice: string }}
 */
export function getOfficeMismatch(template, agent) {
  const rawTemplateOffice = (template && template.office) || '';
  const rawAgentOffice = getAgentOffice(agent);
  const t = canonicalOfficeKey(rawTemplateOffice);
  const a = canonicalOfficeKey(rawAgentOffice);
  const display = {
    templateOffice: prettyOffice(rawTemplateOffice),
    agentOffice: prettyOffice(rawAgentOffice),
  };
  // Global template (no office) or unknown agent office → never a mismatch.
  if (!t || !a) {
    return { mismatch: false, ...display };
  }
  return { mismatch: t !== a, ...display };
}
